<template>
  <div class="transactions-page">
    <!-- 页面标题和操作栏 -->
    <div class="page-header">
      <h2>交易记录管理</h2>
    </div>

    <!-- 查询条件 -->
    <el-card class="filter-card" shadow="never">
      <el-form :model="queryForm" class="filter-form" label-position="top">
        <!-- 第一行：账户相关筛选 -->
        <el-form-item label="账户类型">
          <el-select 
            v-model="queryForm.account_type" 
            placeholder="选择账户类型" 
            clearable 
            class="filter-control"
          >
            <el-option label="现金" value="现金" />
            <el-option label="银行卡" value="银行卡" />
            <el-option label="电子钱包" value="电子钱包" />
            <el-option label="投资账户" value="投资账户" />
            <el-option label="借贷账户" value="借贷账户" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>
        <el-form-item label="账户名称" class="filter-full">
          <AccountSelect
            v-model="queryForm.account_id"
            placeholder="选择或搜索账户"
            class="filter-control"
            include-hidden
            @change="handleSearch"
          />
        </el-form-item>
        <el-form-item label="交易类型">
          <el-select 
            v-model="queryForm.transaction_type" 
            placeholder="选择或搜索类型" 
            clearable 
            filterable
            class="filter-control"
          >
            <el-option label="支出" value="支出" />
            <el-option label="收入" value="收入" />
            <el-option label="转账" value="转账" />
            <el-option label="借入" value="借入" />
            <el-option label="借出" value="借出" />
            <el-option label="收款" value="收款" />
            <el-option label="还款" value="还款" />
          </el-select>
        </el-form-item>
        
        <!-- 第二行：日期相关筛选 -->
        <div class="filter-row">
          <el-form-item label="日期范围">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="filter-date-range"
            />
          </el-form-item>
          
          <!-- 快捷日期选择 -->
          <el-form-item label="快捷选择">
            <el-button-group>
              <el-button size="small" @click="setDateRange('thisYear')">今年</el-button>
              <el-button size="small" @click="setDateRange('lastYear')">去年</el-button>
              <el-button size="small" @click="setDateRange('thisMonth')">本月</el-button>
              <el-button size="small" @click="setDateRange('lastMonth')">上月</el-button>
            </el-button-group>
          </el-form-item>
          
          <el-form-item class="filter-actions">
            <el-button type="primary" @click="handleQuery">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>
        </div>
      </el-form>
    </el-card>

    <!-- 统计信息卡片 -->
    <el-card class="summary-card" shadow="never" v-if="summary">
      <template #header>
        <span>统计信息</span>
      </template>
      <div class="summary-content">
        <!-- 第一行：基础统计 -->
        <div class="summary-item">
          <div class="summary-label">总记录数</div>
          <div class="summary-value">{{ summary.total_count || 0 }}</div>
        </div>
        <div class="summary-item highlight">
          <div class="summary-label">账户余额</div>
          <div class="summary-value" :class="(summary.balance || 0) >= 0 ? 'income' : 'expense'">
            ¥{{ formatAmount(summary.balance || 0) }}
          </div>
        </div>
        <div class="summary-item">
          <div class="summary-label">总收入</div>
          <div class="summary-value income">¥{{ formatAmount(summary.total_income || 0) }}</div>
          <div class="summary-detail">收入+收款+借入</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">总支出</div>
          <div class="summary-value expense">¥{{ formatAmount(summary.total_expense || 0) }}</div>
          <div class="summary-detail">支出+还款+借出</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">收支净额</div>
          <div class="summary-value" :class="(summary.net_flow_amount || 0) >= 0 ? 'income' : 'expense'">
            ¥{{ formatAmount(summary.net_flow_amount || 0) }}
          </div>
          <div class="summary-detail">总收入-总支出</div>
        </div>
        <div class="summary-item" v-if="Math.abs(summary.net_worth_adjustment || 0) > 0.005">
          <div class="summary-label">净资产调整</div>
          <div class="summary-value" :class="(summary.net_worth_adjustment || 0) >= 0 ? 'income' : 'expense'">
            ¥{{ formatAmount(summary.net_worth_adjustment || 0) }}
          </div>
          <div class="summary-detail">账户余额-收支净额</div>
        </div>
        <div class="summary-item" v-if="Math.abs(summary.excluded_net_worth_amount || 0) > 0.005">
          <div class="summary-label">不计净资产账户</div>
          <div class="summary-value" :class="(summary.excluded_net_worth_amount || 0) >= 0 ? 'income' : 'expense'">
            ¥{{ formatAmount(summary.excluded_net_worth_amount || 0) }}
          </div>
          <div class="summary-detail">已从账户余额排除</div>
        </div>
        
        <!-- 第二行：明细分类 -->
        <div class="summary-item">
          <div class="summary-label">纯收入</div>
          <div class="summary-value income">¥{{ formatAmount(summary.income_amount || 0) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">纯支出</div>
          <div class="summary-value expense">¥{{ formatAmount(summary.expense_amount || 0) }}</div>
        </div>
        <div class="summary-item" v-if="summary.transfer_amount > 0">
          <div class="summary-label">转账金额</div>
          <div class="summary-value transfer">¥{{ formatAmount(summary.transfer_amount || 0) }}</div>
          <div class="summary-detail">内部流动</div>
        </div>
        <div class="summary-item" v-if="summary.other_amount > 0">
          <div class="summary-label">其他金额</div>
          <div class="summary-value other">¥{{ formatAmount(summary.other_amount || 0) }}</div>
        </div>
      </div>
    </el-card>

    <!-- 交易记录表格 -->
    <el-card class="table-card" shadow="never">
      <el-table 
        v-loading="loading"
        :data="transactions" 
        stripe 
        border
        class="desktop-transactions-table"
        style="width: 100%"
      >
        <el-table-column prop="transaction_date" label="交易时间" width="160">
          <template #default="{ row }">
            {{ formatDateTime(row.transaction_date) }}
          </template>
        </el-table-column>
        <el-table-column prop="transaction_type" label="类型" width="80">
          <template #default="{ row }">
            <el-tag :type="getTransactionTypeTag(row.transaction_type)">
              {{ row.transaction_type }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="120" align="right">
          <template #default="{ row }">
            <span :class="getAmountClass(row.transaction_type)">
              ¥{{ formatAmount(row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="primary_account_name" label="主账户" width="150" />
        <el-table-column prop="secondary_account_name" label="次账户" width="150" />
        <el-table-column label="分类" width="200">
          <template #default="{ row }">
            <span v-if="row.expense_major_category_name">
              {{ row.expense_major_category_name }}
              <span v-if="row.expense_minor_category_name"> / {{ row.expense_minor_category_name }}</span>
            </span>
            <span v-else-if="row.income_major_category_name">
              {{ row.income_major_category_name }}
              <span v-if="row.income_minor_category_name"> / {{ row.income_minor_category_name }}</span>
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="备注" min-width="200" show-overflow-tooltip />
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" plain @click="handleEdit(row)">
              <el-icon><Edit /></el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-loading="loading" class="mobile-transactions-list">
        <div
          v-for="row in transactions"
          :key="row.id"
          class="mobile-transaction-card"
        >
          <div class="mobile-card-head">
            <div class="mobile-card-type">
              <el-tag :type="getTransactionTypeTag(row.transaction_type)" size="small">
                {{ row.transaction_type }}
              </el-tag>
              <span>{{ formatDateTime(row.transaction_date) }}</span>
            </div>
            <div :class="['mobile-card-amount', getAmountClass(row.transaction_type)]">
              ¥{{ formatAmount(row.amount) }}
            </div>
          </div>

          <div class="mobile-card-body">
            <div class="mobile-info-row">
              <span>主账户</span>
              <strong>{{ row.primary_account_name || '-' }}</strong>
            </div>
            <div class="mobile-info-row" v-if="row.secondary_account_name">
              <span>次账户</span>
              <strong>{{ row.secondary_account_name }}</strong>
            </div>
            <div class="mobile-info-row">
              <span>分类</span>
              <strong>{{ getCategoryText(row) }}</strong>
            </div>
            <div class="mobile-info-row" v-if="row.description">
              <span>备注</span>
              <strong class="desc-clamp">{{ row.description }}</strong>
            </div>
          </div>

          <div class="mobile-card-actions">
            <el-button type="primary" size="small" plain circle @click="handleEdit(row)">
              <el-icon><Edit /></el-icon>
            </el-button>
            <el-button type="danger" size="small" plain circle @click="handleDelete(row)">
              <el-icon><Delete /></el-icon>
            </el-button>
          </div>
        </div>
        <el-empty v-if="!loading && transactions.length === 0" description="暂无交易记录" />
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="queryForm.page"
          :page-size="queryForm.page_size"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          :layout="paginationLayout"
          :small="isMobile"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 新建/编辑对话框 -->
    <TransactionDialog
      v-model="dialogVisible"
      :transaction="currentTransaction"
      :is-edit="isEdit"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Edit, Delete } from '@element-plus/icons-vue'
import api from '../api'
import TransactionDialog from '../components/TransactionDialog.vue'
import AccountSelect from '../components/AccountSelect.vue'
import dayjs from 'dayjs'

export default {
  name: 'Transactions',
  components: {
    TransactionDialog,
    AccountSelect,
    Edit,
    Delete
  },
  setup () {
    const store = useStore()
    
    // 响应式数据
    const loading = ref(false)
    const transactions = ref([])
    const total = ref(0)
    const summary = ref(null)
    const dialogVisible = ref(false)
    const isEdit = ref(false)
    const currentTransaction = ref(null)
    const dateRange = ref([])
    const filteredAccounts = ref([])
    const isMobile = ref(window.innerWidth <= 768)
    
    const queryForm = reactive({
      account_id: null,
      account_type: null,
      transaction_type: null,
      start_date: null,
      end_date: null,
      page: 1,
      page_size: 20
    })

    // 计算属性
    const accounts = computed(() => {
      const allAccounts = store.state.accounts
      const activeAccounts = store.getters.activeAccounts
      console.log('所有账户:', allAccounts)
      console.log('活跃账户:', activeAccounts)
      return activeAccounts
    })

    // 根据账户类型筛选的计算属性
    const accountsByType = computed(() => {
      if (!queryForm.account_type) {
        return accounts.value
      }
      return accounts.value.filter(account => account.account_type === queryForm.account_type)
    })

    const paginationLayout = computed(() => {
      return isMobile.value ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'
    })

    // 监听账户类型变化，更新过滤后的账户列表
    watch([accounts, () => queryForm.account_type], () => {
      filteredAccounts.value = accountsByType.value
      // 如果当前选中的账户不在筛选结果中，清空选择
      if (queryForm.account_id && !accountsByType.value.find(acc => acc.id === queryForm.account_id)) {
        queryForm.account_id = null
      }
    }, { immediate: true })

    // 账户搜索过滤方法
    const filterAccounts = (query) => {
      if (!query) {
        filteredAccounts.value = accountsByType.value
        return
      }
      
      const searchQuery = query.toLowerCase()
      filteredAccounts.value = accountsByType.value.filter(account => {
        return account.account_name.toLowerCase().includes(searchQuery) ||
               account.account_type.toLowerCase().includes(searchQuery)
      })
    }

    // 快捷日期选择方法
    const setDateRange = (type) => {
      const now = dayjs()
      let startDate, endDate
      
      switch (type) {
        case 'thisYear':
          startDate = now.startOf('year').format('YYYY-MM-DD')
          endDate = now.endOf('year').format('YYYY-MM-DD')
          break
        case 'lastYear':
          startDate = now.subtract(1, 'year').startOf('year').format('YYYY-MM-DD')
          endDate = now.subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
          break
        case 'thisMonth':
          startDate = now.startOf('month').format('YYYY-MM-DD')
          endDate = now.endOf('month').format('YYYY-MM-DD')
          break
        case 'lastMonth':
          startDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
          endDate = now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          break
        default:
          return
      }
      
      dateRange.value = [startDate, endDate]
      // 自动触发查询
      handleQuery()
    }

    // 方法
    const loadTransactions = async () => {
      try {
        loading.value = true
        const params = { ...queryForm }
        if (dateRange.value && dateRange.value.length === 2) {
          params.start_date = dateRange.value[0]
          params.end_date = dateRange.value[1]
        }
        const response = await api.getTransactions(params)
        transactions.value = response.items
        total.value = response.total
        summary.value = response.summary // 获取统计信息
      } catch (error) {
        console.error('加载交易记录失败:', error)
      } finally {
        loading.value = false
      }
    }

    const handleQuery = () => {
      queryForm.page = 1
      loadTransactions()
    }

    const handleSizeChange = (size) => {
      queryForm.page_size = size
      queryForm.page = 1
      loadTransactions()
    }

    const handleCurrentChange = (page) => {
      queryForm.page = page
      loadTransactions()
    }

    const handleReset = () => {
      Object.assign(queryForm, {
        account_id: null,
        account_type: null,
        transaction_type: null,
        start_date: null,
        end_date: null,
        page: 1,
        page_size: 20
      })
      dateRange.value = []
      loadTransactions()
    }

    const handleEdit = (row) => {
      currentTransaction.value = { ...row }
      isEdit.value = true
      dialogVisible.value = true
    }

    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除这条交易记录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await api.deleteTransaction(row.id)
        ElMessage.success('删除成功')
        loadTransactions()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
        }
      }
    }

    const handleSaved = () => {
      dialogVisible.value = false
      loadTransactions()
    }

    // 工具方法
    const formatDateTime = (dateTime) => {
      return dayjs(dateTime).format('YYYY-MM-DD HH:mm')
    }

    const formatAmount = (amount) => {
      return parseFloat(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })
    }

    const getTransactionTypeTag = (type) => {
      const tagMap = {
        '支出': 'danger',
        '收入': 'success',
        '转账': 'info',
        '借入': 'warning',
        '借出': 'warning',
        '收款': 'success',
        '还款': 'info'
      }
      return tagMap[type] || ''
    }

    const getAmountClass = (type) => {
      return type === '收入' || type === '借入' || type === '收款' ? 'amount-positive' : 'amount-negative'
    }

    const getCategoryText = (row) => {
      if (row.expense_major_category_name) {
        return [row.expense_major_category_name, row.expense_minor_category_name].filter(Boolean).join(' / ')
      }
      if (row.income_major_category_name) {
        return [row.income_major_category_name, row.income_minor_category_name].filter(Boolean).join(' / ')
      }
      return '-'
    }

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768
    }

    // 生命周期
    onMounted(async () => {
      window.addEventListener('resize', handleResize)
      console.log('Transactions页面加载，开始获取账户数据')
      try {
        await store.dispatch('fetchAccounts')
        console.log('账户数据获取完成')
      } catch (error) {
        console.error('获取账户数据失败:', error)
      }
      loadTransactions()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      loading,
      transactions,
      total,
      summary, // 暴露给模板
      queryForm,
      dateRange,
      accounts,
      filteredAccounts,
      dialogVisible,
      isEdit,
      currentTransaction,
      isMobile,
      paginationLayout,
      loadTransactions,
      handleQuery,
      handleReset,
      filterAccounts,
      setDateRange,
      handleEdit,
      handleDelete,
      handleSaved,
      handleSizeChange,
      handleCurrentChange,
      formatDateTime,
      formatAmount,
      getCategoryText,
      getTransactionTypeTag,
      getAmountClass
    }
  }
}
</script>

<style scoped>
.transactions-page {
  height: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
  font-size: 26px;
}

.filter-card {
  margin-bottom: 20px;
  border-radius: 14px;
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 16px;
}

.filter-form :deep(.el-form-item) {
  margin: 0;
}

.filter-control {
  width: 100%;
}

.filter-row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 260px 1fr auto;
  align-items: end;
  gap: 12px 16px;
}

.filter-date-range {
  width: 100%;
}

.filter-actions :deep(.el-form-item__content) {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
}

.filter-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.filter-card :deep(.el-button-group .el-button) {
  padding: 5px 12px;
  font-size: 12px;
}

.summary-card {
  margin-bottom: 20px;
  border-radius: 14px;
}

.summary-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 14px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 78px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 12px;
}

.summary-item.highlight {
  background: linear-gradient(135deg, #e1f3d8 0%, #f0f9ef 100%);
  border: 1px solid #67c23a;
  position: relative;
  box-shadow: 0 4px 8px rgba(103, 194, 58, 0.2);
}

.summary-item.highlight::before {
  content: "💰";
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 14px;
}

.summary-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  max-width: 100%;
  word-break: break-all;
  text-align: center;
}

.summary-value.income {
  color: #67c23a;
}

.summary-value.expense {
  color: #f56c6c;
}

.summary-value.highlight {
  background-color: #e1f3d8; /* 浅绿色背景 */
  border: 1px solid #a5d6a7; /* 浅绿色边框 */
  padding: 8px 12px; /* 调整内边距 */
  border-radius: 4px;
}

.summary-value.transfer {
  color: #409eff; /* 蓝色 */
}

.summary-value.other {
  color: #909399; /* 灰色 */
}

.summary-detail {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.table-card {
  margin-bottom: 20px;
  border-radius: 14px;
}

.mobile-transactions-list {
  display: none;
}

.pagination-container {
  display: flex;
  justify-content: center;
  overflow-x: auto;
  margin-top: 20px;
  padding-bottom: 4px;
}

.amount-positive {
  color: #67c23a;
  font-weight: bold;
}

.amount-negative {
  color: #f56c6c;
  font-weight: bold;
}

/* 操作按钮样式 */
.el-table .el-table__cell .el-button + .el-button {
  margin-left: 8px;
}

.el-table .el-table__cell .el-button {
  margin-bottom: 4px;
}

.mobile-transaction-card {
  padding: 14px 0;
  border-bottom: 1px solid #ebeef5;
}

.mobile-transaction-card:last-child {
  border-bottom: none;
}

.mobile-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.mobile-card-type {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  color: #909399;
  font-size: 12px;
}

.mobile-card-amount {
  flex-shrink: 0;
  font-size: 17px;
  font-weight: 700;
  text-align: right;
}

.mobile-card-body {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.mobile-info-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  color: #909399;
  font-size: 13px;
}

.mobile-info-row strong {
  min-width: 0;
  color: #303133;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.desc-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mobile-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.mobile-card-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

@media (max-width: 1024px) {
  .filter-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .transactions-page {
    min-height: 100%;
  }

  .page-header {
    margin-bottom: 14px;
  }

  .page-header h2 {
    font-size: 22px;
  }

  .filter-card,
  .summary-card,
  .table-card {
    margin-bottom: 14px;
  }

  .filter-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .filter-full {
    grid-column: 1 / -1;
  }

  .filter-row {
    gap: 12px;
  }

  .filter-card :deep(.el-card__body),
  .summary-card :deep(.el-card__body),
  .table-card :deep(.el-card__body) {
    padding: 14px;
  }

  .filter-card :deep(.el-form-item__label) {
    padding-bottom: 4px;
    line-height: 18px;
  }

  .filter-card :deep(.el-select),
  .filter-card :deep(.el-date-editor) {
    width: 100%;
  }

  .filter-card :deep(.el-button-group) {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    width: 100%;
  }

  .filter-card :deep(.el-button-group .el-button) {
    min-width: 0;
    padding: 8px 0;
  }

  .filter-actions :deep(.el-form-item__content) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }

  .filter-actions :deep(.el-button) {
    width: 100%;
    min-height: 40px;
  }

  .summary-content {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .summary-item {
    min-height: 72px;
    padding: 10px;
  }

  .summary-label,
  .summary-detail {
    font-size: 11px;
  }

  .summary-value {
    font-size: 14px;
  }

  .desktop-transactions-table {
    display: none;
  }

  .mobile-transactions-list {
    display: block;
    min-height: 120px;
  }

  .pagination-container {
    justify-content: center;
    margin-top: 14px;
  }
}

@media (max-width: 360px) {
  .summary-content {
    grid-template-columns: 1fr;
  }

  .mobile-card-head {
    flex-direction: column;
  }

  .mobile-card-amount {
    text-align: left;
  }
}
</style> 
